import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { ScheduleFFHelpers } from '@frontend/schedule-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { styles as dsStyles, NakedButton, Text, Truncated, useModalControl } from '@frontend/design-system';
import { CALENDAR_TRACKING_IDS } from '../../../trackingIds/calendar-tracking-ids';
import { OutOfOfficeEventCardModal } from '../../modals/out-of-office-event-card-modal';
import {
  CALENDAR_VIEW_RIGHT_SPACING,
  HOURS_LANE_WIDTH,
  PROVIDER_COLUMN_GAP,
  PROVIDER_COLUMN_GAP_NEW,
  PROVIDER_LANE_WIDTH,
} from '../constants';
import { useCalendarViewProps } from '../providers';
import { CalendarColumnEntity, CalendarViewOffices, EventData } from '../types';
import { getDurationFromHours, getEventType, isAllDayEvent, moveUnassignedCalendarColumnEntity } from '../utils';

type EventsContainerProps = {
  data: CalendarViewOffices;
  isExpanded: boolean;
};

type EntityColumnsProps = {
  isExpanded: boolean;
  allDayOfficeEventsCount: number;
  columnEntityData: CalendarColumnEntity[];
};

type AllDayOutEventsProps = {
  gridTemplateColumns: string;
};

const BAR_COLLAPSE_HEIGHT = 25; // Height of the bar when collapsed

export const AllDayOutEventsBar = ({ gridTemplateColumns }: AllDayOutEventsProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { data } = useCalendarViewProps();
  const calendarBlockUIFFInfo = ScheduleFFHelpers.useCalendarBlockUIFeatureFlag();

  const hasAllDayEvents = useMemo(
    () =>
      (data ?? []).some((office) => {
        const hasLocationAllDayEvents = office?.officeExceptions?.some((event) =>
          isAllDayEvent(event.startHour, event.endHour)
        );
        // NOTE: Currently, we have all day entity events only for providers
        const hasEntityAllDayEvents = office?.columnEntities?.some((columnEntity) =>
          columnEntity.events.some((event) => isAllDayEvent(event.startHour, event.endHour))
        );
        return hasLocationAllDayEvents || hasEntityAllDayEvents;
      }),
    [data]
  );

  if (!hasAllDayEvents) return null;

  return (
    <AnimatePresence>
      <motion.section
        css={styles.container}
        initial={{ height: 0 }}
        animate={{ height: isExpanded ? 'auto' : BAR_COLLAPSE_HEIGHT }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        exit={{ height: 0 }}
        key={'container'}
        onClick={() => setIsExpanded(!isExpanded)}
        data-trackingid={CALENDAR_TRACKING_IDS.allDayOutToggleExpandButton}
      >
        <motion.div
          key={isExpanded ? 'expanded' : 'collapsed'}
          initial={{ opacity: 0.5 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0.5 }}
          transition={{ duration: 1, ease: 'linear' }}
          css={styles.expandButtonStickyContainer}
        >
          <NakedButton css={styles.expandButton}>
            <Icon name={isExpanded ? 'collapse-small' : 'expand-small'} />
          </NakedButton>
        </motion.div>
        <div css={styles.eventsLayout(gridTemplateColumns, calendarBlockUIFFInfo.isEnabled)}>
          <EventsContainer data={data} isExpanded={isExpanded} />
        </div>
      </motion.section>
    </AnimatePresence>
  );
};

const EventsContainer = ({ data, isExpanded }: EventsContainerProps) => {
  return (
    <>
      {data?.map((office, idx) => {
        if (office.columnEntities?.length === 0) return null; // Do not show column if there is no column entity

        const sortedColumnEntities = moveUnassignedCalendarColumnEntity(office.columnEntities);
        const allDayOfficeEvents = office.officeExceptions?.filter((officeEvent) =>
          isAllDayEvent(officeEvent.startHour, officeEvent.endHour)
        );

        return (
          <div key={`${office.officeName}-${idx}`} css={styles.eventsContainer}>
            <AnimatePresence>
              {isExpanded &&
                allDayOfficeEvents?.map((officeEvent) => {
                  return <EventCard key={officeEvent.eventId} event={officeEvent} />;
                })}
              <EntityColumns
                isExpanded={isExpanded}
                columnEntityData={sortedColumnEntities}
                allDayOfficeEventsCount={allDayOfficeEvents?.length || 0}
              />
            </AnimatePresence>
          </div>
        );
      })}
    </>
  );
};

const EntityColumns = ({ isExpanded, columnEntityData, allDayOfficeEventsCount }: EntityColumnsProps) => {
  const { t } = useTranslation('schedule');

  return (
    <section css={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {columnEntityData.map((columnEntity, idx) => {
        const allDayEntityEvents = columnEntity.events.filter((event) => isAllDayEvent(event.startHour, event.endHour));
        const totalEvents = allDayEntityEvents.length + allDayOfficeEventsCount;

        return (
          <section key={columnEntity.entityName + idx} css={entityColumnStyles.column}>
            <div css={entityColumnStyles.content}>
              {isExpanded ? (
                allDayEntityEvents.map((event) => {
                  return <EventCard key={event.id} event={event} columnEntity={columnEntity} />;
                })
              ) : (
                <motion.div
                  key='collapsed'
                  css={[dsStyles.flexCenter, entityColumnStyles.columnBorder]}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 30 }}
                  transition={{ duration: 0.5, ease: 'easeInOut' }}
                >
                  {totalEvents > 0 && (
                    <Text size='small' weight='bold'>
                      {t('{{count}} events', { count: totalEvents })}
                    </Text>
                  )}
                </motion.div>
              )}
            </div>
          </section>
        );
      })}
    </section>
  );
};

const EventCard = ({ event, columnEntity }: { event: EventData; columnEntity?: CalendarColumnEntity }) => {
  const { getScopeName } = useAppScopeStore();
  const { triggerProps, modalProps } = useModalControl();
  const { id = '', locationId = '', name, startDate, endDate } = event;
  const { entityId, entityName: providerName } = columnEntity || {};

  return (
    <>
      <motion.div
        key='expanded'
        data-trackingid={CALENDAR_TRACKING_IDS.allDayOutEventCard}
        {...triggerProps}
        css={[styles.eventCard, !columnEntity && { marginBottom: theme.spacing(0.5) }]}
        initial={{ opacity: 0, y: -5 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -5 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        whileHover={{ scale: 1.001, boxShadow: theme.shadows.floating }}
      >
        <Icon name={entityId ? 'remove-small' : 'location-small'} css={{ minWidth: 'fit-content' }} />
        <Truncated as={Text} size='small' weight='bold'>
          {event.name}
        </Truncated>
      </motion.div>
      <OutOfOfficeEventCardModal
        id={id}
        title={name}
        eventType={getEventType(event)}
        duration={getDurationFromHours(event.startHour, event.endHour)}
        locationName={getScopeName(locationId)}
        startDate={startDate}
        endDate={endDate}
        modalProps={modalProps}
        providerId={entityId}
        providerName={providerName}
      />
    </>
  );
};

const styles = {
  container: css`
    border-top: 1px solid ${theme.colors.neutral20};
    background-color: ${theme.colors.neutral5};
    display: flex;
    margin-right: ${CALENDAR_VIEW_RIGHT_SPACING}px;
    cursor: pointer;
  `,
  expandButtonStickyContainer: css`
    width: ${HOURS_LANE_WIDTH}px;
    position: sticky;
    top: 0;
    left: 0;
    background: ${theme.colors.neutral5};
    z-index: 1;
  `,
  expandButton: css`
    display: flex;
    justify-content: flex-end;
    height: 100%;
    width: 100%;
    padding: ${theme.spacing(0.5, 1)};
  `,
  eventsLayout: (gridTemplateColumns: string, providerColumnGap: boolean) => css`
    display: grid;
    grid-template-columns: ${gridTemplateColumns};
    grid-template-rows: auto;
    flex: 1;
    gap: ${providerColumnGap ? PROVIDER_COLUMN_GAP_NEW : PROVIDER_COLUMN_GAP}px;
  `,
  eventsContainer: css`
    display: flex;
    flex-direction: column;
  `,
  eventCard: css`
    display: flex;
    align-items: center;
    border-left: 4px solid ${theme.colors.neutral60};
    background-color: ${theme.colors.neutral20};
    border-radius: 2px;
    padding: ${theme.spacing(0.25, 1)};
    gap: ${theme.spacing(0.5)};
    width: 100%;
    box-shadow: ${theme.shadows.light};
    cursor: pointer;
    &:first-of-type {
      margin-top: ${theme.spacing(0.5)};
    }
    &:last-of-type {
      margin-bottom: ${theme.spacing(0.5)};
    }
  `,
};

const entityColumnStyles = {
  column: css`
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    gap: ${theme.spacing(1)};
    min-width: ${PROVIDER_LANE_WIDTH}px;
  `,
  content: css`
    display: flex;
    flex-direction: column;
    width: calc(100% - 16px);
    height: 100%;
    gap: ${theme.spacing(0.5)};
  `,
  columnBorder: css`
    flex: 1;
    position: relative;
    &::before {
      content: '';
      position: absolute;
      right: ${theme.spacing(-1)};
      top: 0;
      width: 1px;
      height: 100%;
      box-shadow: 1px 0 0 ${theme.colors.neutral20};
    }
  `,
};
